import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";

export default function ProtectedRoute({ children }) {
  const { session, loading, clinics, clinicsLoading, clinicsFetched, clinicsError, refreshClinics } = useAuth();
  const impersonation = useImpersonation();
  const location = useLocation();

  if (loading || impersonation.loading || ((session || impersonation.active) && !clinicsFetched)) {
    return (
      <div data-testid="auth-loading" className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (!session && !impersonation.active) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // RPC failed AND we have no cached clinics — show a recoverable state
  // instead of bouncing the user to /signup/clinic. Common cause: the
  // Emergent devtools wrapper throwing DataCloneError on the first rpc
  // after a page load.
  if (clinicsError && clinics.length === 0) {
    return (
      <div data-testid="auth-clinics-error" className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <h2 className="text-base font-semibold text-slate-900">Couldn't load your clinics.</h2>
          <p className="text-sm text-slate-500 mt-2">A transient network or auth hiccup. Try again — your session is intact.</p>
          <button
            type="button"
            onClick={() => refreshClinics()}
            disabled={clinicsLoading}
            data-testid="auth-clinics-retry"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
          >
            {clinicsLoading ? "Retrying…" : "Retry"}
          </button>
        </div>
      </div>
    );
  }

  // Authenticated user with zero memberships hasn't finished signup
  // (e.g., wizard step 2 errored). Send them to clinic creation.
  if (!clinicsLoading && clinics.length === 0) {
    const pendingInviteCode = window.sessionStorage.getItem("dental_os_pending_invite_code");
    if (pendingInviteCode) {
      return <Navigate to={`/join?code=${encodeURIComponent(pendingInviteCode)}`} replace />;
    }
    if (location.pathname !== "/signup/clinic") {
      return <Navigate to="/signup/clinic" replace />;
    }
  }

  return children;
}
